import Button from "@/components/Button";
import { canonicalUrl } from "@/config/canonical";
import { HeroSection } from "@/containers/HeroSection";
import { mixpanelTrack } from "@/events/mixpanelTrack";
import { useGoBack } from "@/hooks/useGoBack";
import { cn } from "@/lib/utils";
import { getVersionAssetsSelector } from "@/redux/loadedContent/loadedContentSlice";
import { addAlert } from "@/redux/message/messageSlice";
import { versionNameBySlug } from "@/redux/players/playersSlice";
import type { AppDispatch } from "@/redux/store";
import { useTranslation } from "@repo/shared/i18n";
import {
  URLizeString,
  capitalizeWordsAndRemoveDashes,
} from "@repo/shared/utils";
import { Copy, Download, Share } from "lucide-react";
import React, { Suspense, useEffect, useMemo, useState } from "react";
import { isMobile } from "react-device-detect";
import { useDispatch, useSelector } from "react-redux";
import { Await, useLoaderData, useLocation } from "react-router";

import type {
  AspectPlayerImageType,
  ImageType,
  PlayerImageResponse,
  PlayerImagesLoaderResponse,
  PlayerType,
} from "./playerImagesLoader";

type TrackingData = {
  playerType: PlayerType;
  evolutionChain: string;
  evoPathId: string;
  resourceId: string;
  name: string;
  version: string;
};

export const PlayerImages = () => {
  const { pathname, search } = useLocation();
  // The player card these assets belong to — this URL without its /images leaf.
  // The route tree cannot supply it: /images is a sibling child of the list,
  // not a child of the detail page, so its route parent is the list itself.
  const goBack = useGoBack({
    fallback: pathname.replace(/\/images$/, "") + search,
  });
  const {
    resourceId,
    name,
    version,
    evolutionChain,
    evoPathId,
    isEvoBuilder,
    playerImages,
  } = useLoaderData() as PlayerImagesLoaderResponse;
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation("players");
  const [trackingData, setTrackingData] = useState<TrackingData>({
    playerType: "player",
    evolutionChain: "",
    evoPathId: "",
    resourceId: "",
    name: "",
    version: "",
  });

  const isEvolutionPlayer = useMemo(() => {
    return evolutionChain !== null;
  }, [evolutionChain]);

  const isEvolvedPlayer = useMemo(() => {
    return evoPathId !== null;
  }, [evoPathId]);

  const versionAssets = useSelector(getVersionAssetsSelector);
  const { displayName, displayVersion } = useMemo(() => {
    return {
      displayName: capitalizeWordsAndRemoveDashes(name || ""),
      displayVersion:
        versionNameBySlug(versionAssets, version) ??
        (version ? capitalizeWordsAndRemoveDashes(version) : ""),
    };
  }, [name, version, versionAssets]);

  useEffect(() => {
    let playerTypeData: PlayerType;
    if (isEvoBuilder && isEvolutionPlayer) {
      playerTypeData = "evo-builder";
    } else if (isEvolutionPlayer) {
      playerTypeData = "evolution";
    } else if (isEvolvedPlayer) {
      playerTypeData = "evolved";
    } else {
      playerTypeData = "player";
    }
    const data = {
      playerType: playerTypeData,
      evolutionChain,
      evoPathId,
      resourceId,
      name: displayName,
      version: displayVersion,
    };

    setTrackingData(data);
    mixpanelTrack("player-images-page-viewed", data);
  }, []);

  const handleDownload = async ({
    imageUrl,
    imageTitle,
    aspect,
    playerImageType,
  }: CopyOrDownloadOptions) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const ext = blob.type.split("/")[1] || "png";

      const filename = `${imageTitle}.${ext}`;

      if (isMobile) {
        const file = new File([blob], filename, { type: blob.type });
        await navigator.share({ files: [file] });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
      }

      mixpanelTrack("player-image-downloaded", {
        isMobile,
        aspect,
        imageTitle,
        playerImageType,
        ...trackingData,
      });

      dispatch(
        addAlert({
          description: t("playerImages.alerts.imageDownloaded"),
          type: "success",
        }),
      );
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") return;
      console.error("Failed to download image:", err);
      dispatch(
        addAlert({
          title: t("playerImages.alerts.errorTitle"),
          description: t("playerImages.alerts.downloadFailed"),
          type: "error",
        }),
      );
    }
  };

  const handleCopyToClipboard = async ({
    imageUrl,
    imageTitle,
    aspect,
    playerImageType,
  }: CopyOrDownloadOptions) => {
    try {
      const blobPromise = fetch(imageUrl).then((res) => res.blob());

      await navigator.clipboard.write([
        new ClipboardItem({
          "image/png": blobPromise.then(
            (blob) => new Blob([blob], { type: "image/png" }),
          ),
        }),
      ]);

      mixpanelTrack("player-image-copied-to-clipboard", {
        isMobile,
        aspect,
        imageTitle,
        playerImageType,
        ...trackingData,
      });

      dispatch(
        addAlert({
          description: t("playerImages.alerts.imageCopied"),
          type: "success",
        }),
      );
    } catch (err) {
      console.error("Failed to copy image:", err);
      dispatch(
        addAlert({
          title: t("playerImages.alerts.errorTitle"),
          description: t("playerImages.alerts.copyFailed"),
          type: "error",
        }),
      );
    }
  };

  return (
    <React.Fragment key={`player-images-${resourceId}`}>
      <HeroSection
        back={goBack}
        helmetTitle={t("playerImages.helmetTitle", {
          name: displayName,
          version: displayVersion ? ` - ${displayVersion}` : "",
        })}
        helmetDescription={t("playerImages.helmetDescription")}
        helmetCanonicalLink={canonicalUrl("/player-assets")}
        shouldBeInTheDocumentFlow
      />
      <main className="min-h-screen mx-auto max-w-content flex flex-col gap-3 px-4 touch-device:px-16 transition-all duration-300 pb-4 touch-device:pb-16">
        <div className="touch-device:flex flex-col gap-2 mt-5 touch-device:mt-10 touch-device:mb-5">
          <h1 className="text-lg touch-device:text-3xl font-bold hidden touch-device:block">
            {t("playerImages.title")}
          </h1>
          <h2 className="text-sm touch-device:text-base text-dark-tertiary">
            {t("playerImages.description")}
          </h2>
        </div>
        {isEvolutionPlayer && (
          <PlayerTypeContainer
            title={t("playerImages.sections.evolutionChain")}
          >
            <Suspense
              fallback={
                <div className="flex flex-wrap gap-5">
                  <ImageContainer
                    playerImageType="with-evolution-chain"
                    title="16:9"
                    description={t("playerImages.greatForXTwitch")}
                    aspect="aspect-16/9"
                    alt={`${displayName} ${displayVersion ? ` - ${displayVersion}` : ""} - 16:9`}
                    loading={true}
                    imageTitle={generateImageTitle(
                      displayName,
                      displayVersion,
                      "16:9",
                    )}
                  />
                  <ImageContainer
                    playerImageType="with-evolution-chain"
                    title="1:1"
                    description={t("playerImages.greatForMobile")}
                    aspect="aspect-square"
                    alt={`${displayName} ${displayVersion ? ` - ${displayVersion}` : ""} - 1:1`}
                    loading={true}
                    imageTitle={generateImageTitle(
                      displayName,
                      displayVersion,
                      "1:1",
                    )}
                  />
                </div>
              }
            >
              <Await resolve={playerImages}>
                {(images: PlayerImageResponse[]) => {
                  const playerImageType = EVOLUTION_CHAIN_IMAGE;
                  return (
                    <div className="flex flex-wrap gap-5">
                      <ImageContainer
                        playerImageType={playerImageType}
                        imageUrl={getPlayerImage({
                          aspect: "16:9",
                          playerImageType,
                          playerImages: images,
                        })}
                        title="16:9"
                        description={t("playerImages.greatForXTwitch")}
                        aspect="aspect-16/9"
                        alt={`${displayName} ${displayVersion ? ` - ${displayVersion}` : ""} - 16:9`}
                        imageTitle={generateImageTitle(
                          displayName,
                          displayVersion,
                          "16:9",
                        )}
                        handleDownload={({
                          imageUrl,
                          imageTitle,
                          aspect,
                        }: CopyOrDownloadOptions) =>
                          handleDownload({
                            imageUrl,
                            imageTitle,
                            aspect,
                            playerImageType,
                          })
                        }
                        handleCopyToClipboard={({
                          imageUrl,
                          imageTitle,
                          aspect,
                        }: CopyOrDownloadOptions) =>
                          handleCopyToClipboard({
                            imageUrl,
                            imageTitle,
                            aspect,
                            playerImageType,
                          })
                        }
                      />
                      <ImageContainer
                        playerImageType={playerImageType}
                        imageUrl={getPlayerImage({
                          aspect: "1:1",
                          playerImageType,
                          playerImages: images,
                        })}
                        title="1:1"
                        description={t("playerImages.greatForMobile")}
                        aspect="aspect-square"
                        alt={`${displayName} ${displayVersion ? ` - ${displayVersion}` : ""} - 1:1`}
                        imageTitle={generateImageTitle(
                          displayName,
                          displayVersion,
                          "1:1",
                        )}
                        handleDownload={({
                          imageUrl,
                          imageTitle,
                          aspect,
                        }: CopyOrDownloadOptions) =>
                          handleDownload({
                            imageUrl,
                            imageTitle,
                            aspect,
                            playerImageType,
                          })
                        }
                        handleCopyToClipboard={({
                          imageUrl,
                          imageTitle,
                          aspect,
                        }: CopyOrDownloadOptions) =>
                          handleCopyToClipboard({
                            imageUrl,
                            imageTitle,
                            aspect,
                            playerImageType,
                          })
                        }
                      />
                    </div>
                  );
                }}
              </Await>
            </Suspense>
          </PlayerTypeContainer>
        )}
        <PlayerTypeContainer title={t("playerImages.sections.detailedCard")}>
          <Suspense
            fallback={
              <div className="flex flex-wrap gap-5">
                <ImageContainer
                  playerImageType="detailed-player-card"
                  title="1:1"
                  description={t("playerImages.greatForMobile")}
                  aspect="aspect-square"
                  alt={`${displayName} ${displayVersion ? ` - ${displayVersion}` : ""} - 1:1`}
                  loading={true}
                  imageTitle={generateImageTitle(
                    displayName,
                    displayVersion,
                    "1:1",
                  )}
                />
                <ImageContainer
                  playerImageType="detailed-player-card"
                  title="16:9"
                  description={t("playerImages.greatForXTwitch")}
                  aspect="aspect-16/9"
                  alt={`${displayName} ${displayVersion ? ` - ${displayVersion}` : ""} - 16:9`}
                  loading={true}
                  imageTitle={generateImageTitle(
                    displayName,
                    displayVersion,
                    "16:9",
                  )}
                />
                <ImageContainer
                  playerImageType="detailed-player-card"
                  title={t("playerImages.simpleView")}
                  description={t("playerImages.greatForOverview")}
                  aspect="aspect-2/3"
                  alt={`${displayName} ${displayVersion ? ` - ${displayVersion}` : ""} - 2:3`}
                  loading={true}
                  imageTitle={generateImageTitle(
                    displayName,
                    displayVersion,
                    "2:3",
                  )}
                />
              </div>
            }
          >
            <Await resolve={playerImages}>
              {(images: PlayerImageResponse[]) => {
                const playerImageType = DETAILED_CARD_IMAGE;
                return (
                  <div className="flex flex-wrap gap-5">
                    <ImageContainer
                      playerImageType={playerImageType}
                      imageUrl={getPlayerImage({
                        aspect: "1:1",
                        playerImageType,
                        playerImages: images,
                      })}
                      title="1:1"
                      description={t("playerImages.greatForMobile")}
                      aspect="aspect-square"
                      alt={`${displayName} ${displayVersion ? ` - ${displayVersion}` : ""} - 1:1`}
                      imageTitle={generateImageTitle(
                        displayName,
                        displayVersion,
                        "1:1",
                      )}
                      handleDownload={({
                        imageUrl,
                        imageTitle,
                        aspect,
                      }: CopyOrDownloadOptions) =>
                        handleDownload({
                          imageUrl,
                          imageTitle,
                          aspect,
                          playerImageType,
                        })
                      }
                      handleCopyToClipboard={({
                        imageUrl,
                        imageTitle,
                        aspect,
                      }: CopyOrDownloadOptions) =>
                        handleCopyToClipboard({
                          imageUrl,
                          imageTitle,
                          aspect,
                          playerImageType,
                        })
                      }
                    />
                    <ImageContainer
                      playerImageType={playerImageType}
                      imageUrl={getPlayerImage({
                        aspect: "16:9",
                        playerImageType,
                        playerImages: images,
                      })}
                      title="16:9"
                      description={t("playerImages.greatForXTwitch")}
                      aspect="aspect-16/9"
                      alt={`${displayName} ${displayVersion ? ` - ${displayVersion}` : ""} - 16:9`}
                      imageTitle={generateImageTitle(
                        displayName,
                        displayVersion,
                        "16:9",
                      )}
                      handleDownload={({
                        imageUrl,
                        imageTitle,
                        aspect,
                      }: CopyOrDownloadOptions) =>
                        handleDownload({
                          imageUrl,
                          imageTitle,
                          aspect,
                          playerImageType,
                        })
                      }
                      handleCopyToClipboard={({
                        imageUrl,
                        imageTitle,
                        aspect,
                      }: CopyOrDownloadOptions) =>
                        handleCopyToClipboard({
                          imageUrl,
                          imageTitle,
                          aspect,
                          playerImageType,
                        })
                      }
                    />{" "}
                    <ImageContainer
                      playerImageType={playerImageType}
                      imageUrl={getPlayerImage({
                        aspect: "2:3",
                        playerImageType,
                        playerImages: images,
                      })}
                      title={t("playerImages.simpleView")}
                      description={t("playerImages.greatForOverview")}
                      aspect="aspect-2/3"
                      alt={`${displayName} ${displayVersion ? ` - ${displayVersion}` : ""} - 2:3`}
                      imageTitle={generateImageTitle(
                        displayName,
                        displayVersion,
                        "2:3",
                      )}
                      handleDownload={({
                        imageUrl,
                        imageTitle,
                        aspect,
                      }: CopyOrDownloadOptions) =>
                        handleDownload({
                          imageUrl,
                          imageTitle,
                          aspect,
                          playerImageType,
                        })
                      }
                      handleCopyToClipboard={({
                        imageUrl,
                        imageTitle,
                        aspect,
                      }: CopyOrDownloadOptions) =>
                        handleCopyToClipboard({
                          imageUrl,
                          imageTitle,
                          aspect,
                          playerImageType,
                        })
                      }
                    />
                  </div>
                );
              }}
            </Await>
          </Suspense>
        </PlayerTypeContainer>
      </main>
    </React.Fragment>
  );
};

const getPlayerImage = ({
  aspect,
  playerImageType,
  playerImages,
}: {
  aspect: AspectPlayerImageType;
  playerImageType: ImageType;
  playerImages: PlayerImageResponse[];
}) => {
  return playerImages.find(
    (img) =>
      img.aspectRatio === aspect && img.playerImageType === playerImageType,
  )?.url;
};

// Hoisted: string literals inside the render callbacks trip the i18n lint.
const EVOLUTION_CHAIN_IMAGE: ImageType = "with-evolution-chain";
const DETAILED_CARD_IMAGE: ImageType = "detailed-player-card";

const generateImageTitle = (
  displayName: string,
  displayVersion: string,
  aspect: string,
) => {
  return `${URLizeString(displayName)}-${URLizeString(displayVersion)}-${URLizeString(aspect)}`;
};
interface PlayerTypeContainerProps {
  title: string;
  children: React.ReactNode;
}
const PlayerTypeContainer = ({ title, children }: PlayerTypeContainerProps) => (
  <article className="touch-device:bg-panel-background touch-device:border touch-device:border-panel-middleground rounded-[20px] touch-device:p-12 flex flex-col gap-4 touch-device:gap-5 mt-4">
    <h3 className="text-xl touch-device:text-2xl font-bold">{title}</h3>
    {children}
  </article>
);

type CopyOrDownloadOptions = {
  imageUrl: string;
  imageTitle: string;
  aspect: "aspect-square" | "aspect-16/9" | "aspect-2/3";
  playerImageType: ImageType;
};

interface ImageContainerProps {
  title: string;
  description: string;
  imageTitle: string;
  aspect: "aspect-square" | "aspect-16/9" | "aspect-2/3";
  alt: string;
  handleDownload?: ({
    imageUrl,
    imageTitle,
    aspect,
    playerImageType,
  }: CopyOrDownloadOptions) => void;
  handleCopyToClipboard?: ({
    imageUrl,
    imageTitle,
    aspect,
    playerImageType,
  }: CopyOrDownloadOptions) => void;
  imageUrl?: string;
  loading?: boolean;
  playerImageType: ImageType;
}

const ImageContainer = ({
  title,
  imageTitle,
  imageUrl,
  description,
  aspect,
  alt,
  handleDownload,
  handleCopyToClipboard,
  loading,
  playerImageType,
}: ImageContainerProps) => {
  const { t } = useTranslation(["players", "common"]);
  return (
    <article className="flex flex-col w-full bg-panel-middleground rounded-lg p-5 max-w-125 gap-2.5">
      <div className="flex flex-col gap-y-1.5">
        <h3 className="font-semibold">{title}</h3>
        <p className="text-sm text-dark-tertiary">{description}</p>
      </div>
      <div
        className={cn(
          "flex h-full max-h-64 self-start overflow-hidden bg-panel-base relative",
          aspect,
          aspect === "aspect-2/3" && "rounded-lg",
          loading && "animate-pulse",
        )}
      >
        {loading && (
          <div className="text-sm text-dark-tertiary m-auto animate-pulse text-center px-4">
            {t("playerImages.loading")}
          </div>
        )}
        {imageUrl && (
          <a
            href={imageUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block size-full cursor-pointer"
          >
            <img src={imageUrl} alt={alt} className="size-full object-cover" />
          </a>
        )}
      </div>
      <div className={cn("flex flex-row gap-x-2.5", !imageUrl && "invisible")}>
        <Button
          variant="primary"
          label={isMobile ? t("common:share") : t("common:download")}
          onClick={() =>
            handleDownload({ imageUrl, imageTitle, aspect, playerImageType })
          }
          icon={isMobile ? <Share size={20} /> : <Download size={20} />}
          iconBeforeText
        />
        <Button
          variant="secondary"
          label={t("playerImages.copyToClipboard")}
          onClick={() =>
            handleCopyToClipboard({
              imageUrl,
              imageTitle,
              aspect,
              playerImageType,
            })
          }
          icon={<Copy size={20} />}
          iconBeforeText
        />
      </div>
    </article>
  );
};
